import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Link,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../../services/api';
import * as XLSX from 'xlsx';
import { generateSignedUrlForFilename } from '../../../utils/s3Utils';

const SEZApplications = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Alert state for notifications
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Filter states
  const [filters, setFilters] = useState({
    labType: '',
    userName: '',
    dateFrom: null,
    dateTo: null
  });

  const labTypes = [
    { value: '1', label: 'Testing' },
    { value: '2', label: 'Calibration' },
    { value: '3', label: 'Medical' },
    { value: '4', label: 'PTP' },
    { value: '5', label: 'RMP' }
  ];

  // Fetch data function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        labType: filters.labType || null,
        userName: filters.userName || null,
        dateFrom: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null,
        dateTo: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null
      };

      // Remove null values
      Object.keys(params).forEach(key => params[key] === null && delete params[key]);

      const response = await api.get('/accounts/sez-applications', { params });
      
      setData(response.data.data || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching SEZ applications data:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setPage(0); // Reset to first page when filtering
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      labType: '',
      userName: '',
      dateFrom: null,
      dateTo: null
    });
    setPage(0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Excel download
  const handleExcelDownload = async () => {
    try {
      const params = {
        labType: filters.labType || null,
        userName: filters.userName || null,
        dateFrom: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : null,
        dateTo: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : null,
        exportAll: true
      };

      // Remove null values
      Object.keys(params).forEach(key => params[key] === null && delete params[key]);

      const response = await api.get('/accounts/sez-applications', { params });
      const exportData = response.data.data || [];

      if (exportData.length === 0) {
        alert('No data to download');
        return;
      }

      // Prepare data for Excel export
      const excelData = exportData.map((item, index) => ({
        'S. No': index + 1,
        'Lab ID': item.applicationNo,
        'Laboratory Name': item.laboratoryName,
        'Address': item.address,
        'GSTIN': item.gstin,
        'SEZ File': item.sezfile ? 'Available' : 'Not Available'
      }));

      // Create and download Excel file
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SEZ Applications Report');
      XLSX.writeFile(wb, `SEZ_Applications_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error downloading Excel:', err);
      alert('Error downloading file');
    }
  };

  // Handle file view
  const handleViewFile = async (fileName) => {
    if (!fileName) {
      showAlert('No file available to view', 'warning');
      return;
    }

    try {
      setLoading(true);
      // Generate signed URL for the file
      const signedUrl = await generateSignedUrlForFilename(fileName);
      
      if (signedUrl) {
        // Open the signed URL in a new tab
        window.open(signedUrl, '_blank');
      } else {
        showAlert('Unable to generate file access URL', 'error');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      showAlert('Error accessing file: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to show alerts
  const showAlert = (message, severity = 'info') => {
    setAlert({ open: true, message, severity });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          SEZ Applications
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filter</Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Lab Type</InputLabel>
                <Select
                  value={filters.labType}
                  label="Lab Type"
                  onChange={(e) => handleFilterChange('labType', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {labTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="User Name"
                value={filters.userName}
                onChange={(e) => handleFilterChange('userName', e.target.value)}
                placeholder="Enter UserName"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Date From"
                value={filters.dateFrom}
                onChange={(newValue) => handleFilterChange('dateFrom', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                inputFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Date To"
                value={filters.dateTo}
                onChange={(newValue) => handleFilterChange('dateTo', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                inputFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="info"
              onClick={handleResetFilters}
              startIcon={<RefreshIcon />}
            >
              Reset Filter
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExcelDownload}
              startIcon={<DownloadIcon />}
              disabled={loading}
            >
              Download Excel
            </Button>
          </Box>
        </Paper>

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>S. No</TableCell>
                      <TableCell>Lab ID</TableCell>
                      <TableCell>UserName</TableCell>
                      <TableCell>Laboratory Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>GSTIN</TableCell>
                      <TableCell>SEZ File</TableCell>
                      <TableCell>Submit Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row, index) => (
                      <TableRow
                        key={row.id || index}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary">
                            {row.applicationNo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="secondary">
                            {row.loginname}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.laboratoryName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.gstin}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.sezfile ? (
                            <Tooltip title="View/Download SEZ File">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewFile(row.sezfile)}
                                disabled={loading}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No file
                            </Typography>
                          )}
                        </TableCell>
                         <TableCell>
                          <Typography variant="body2">
                            {row.createdDate ? new Date(row.createdDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="h6" color="text.secondary" sx={{ py: 4 }}>
                            No Data Found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalCount > 0 && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </>
          )}
        </Paper>

        {/* Alert Snackbar */}
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={() => setAlert(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setAlert(prev => ({ ...prev, open: false }))}
            severity={alert.severity}
            sx={{ width: '100%' }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default SEZApplications;